import React, { useState, useEffect, useRef } from 'react';
import { Card, DatePicker, Button, Table, Row, Col, Statistic, Select, message } from 'antd';
import { FilePdfOutlined, PrinterOutlined } from '@ant-design/icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ApiService from '../services/api';

const { RangePicker } = DatePicker;
const { Option } = Select;

const ProfitLoss = () => {
  const [dateRange, setDateRange] = useState(null);
  const [selectedTruck, setSelectedTruck] = useState('all');
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [plData, setPlData] = useState(null);
  const [expenseBreakdown, setExpenseBreakdown] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const reportRef = useRef();

  useEffect(() => {
    loadFilters();
  }, []);

  const loadFilters = async () => {
    try {
      const [driversData, trucksResponse] = await Promise.all([
        ApiService.getDrivers(),
        ApiService.getTrucks()
      ]);
      setDrivers(Array.isArray(driversData) ? driversData : []);
      setTrucks(Array.isArray(trucksResponse?.trucks) ? trucksResponse.trucks : []);
    } catch (error) {
      console.error('Error loading filters:', error);
      setDrivers([]);
      setTrucks([]);
    }
  };

  const generatePL = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.error('Please select a date range');
      return;
    }
    
    setLoading(true);
    try {
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');
      const truckId = selectedTruck === 'all' ? null : selectedTruck;

      const [incomeData, expensesData] = await Promise.all([
        ApiService.getGrossIncomeReport(null, startDate, endDate, truckId, true),
        ApiService.getExpensesReport(null, startDate, endDate, truckId, true)
      ]);

      let totalRevenue = 0;
      if (incomeData.total_revenue !== undefined) {
        totalRevenue = incomeData.total_revenue || 0;
      } else if (incomeData.revenue_by_driver) {
        totalRevenue = Object.values(incomeData.revenue_by_driver).reduce((sum, rev) => sum + rev, 0);
      }
      
      const totalFuelCosts = parseFloat(expensesData.fuel_expenses) || 0;
      const totalOtherExpenses = parseFloat(expensesData.other_expenses) || 0;
      const totalExpenses = totalFuelCosts + totalOtherExpenses;
      const netProfit = totalRevenue - totalExpenses;

      // Process expense breakdown as category summaries
      const expenseRecords = expensesData.expense_records || [];
      const categoryTotals = {};
      
      expenseRecords.forEach(record => {
        const category = (record.expense_type || 'OTHER').toUpperCase();
        if (['POLICE', 'VID', 'OTHER'].includes(category)) {
          categoryTotals[category] = (categoryTotals[category] || 0) + (parseFloat(record.amount) || 0);
        }
      });
      
      const expenseBreakdownData = Object.entries(categoryTotals)
        .filter(([category, amount]) => amount > 0)
        .map(([category, amount]) => ({
          category,
          amount
        }));
      
      setExpenseBreakdown(expenseBreakdownData);

      const selectedTruckName = selectedTruck === 'all' ? 'All Trucks' : 
        trucks.find(t => t.truck_id === selectedTruck)?.license_plate || 'Unknown Truck';

      setPlData({
        revenue: totalRevenue,
        fuelCosts: totalFuelCosts,
        otherExpenses: totalOtherExpenses,
        totalExpenses: totalExpenses,
        netProfit: netProfit,
        profitMargin: totalRevenue > 0 ? ((netProfit / totalRevenue) * 100).toFixed(2) : 0,
        startDate,
        endDate,
        driverName: selectedTruckName
      });
    } catch (error) {
      console.error('Error generating P&L:', error);
      message.error('Failed to generate P&L report');
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!plData) {
      message.error('Please generate a report first');
      return;
    }

    setPdfLoading(true);
    try {
      const element = reportRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const fileName = `PL_Report_${plData.startDate}_to_${plData.endDate}.pdf`;
      pdf.save(fileName);
      message.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error('Failed to generate PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePrint = () => {
    if (!plData) {
      message.error('Please generate a report first');
      return;
    }
    window.print();
  };

  const plTableData = plData ? [
    { category: 'REVENUE', subcategory: 'Trip Income', amount: plData.revenue, isRevenue: true },
    { category: 'EXPENSES', subcategory: 'Fuel Costs', amount: plData.fuelCosts, isRevenue: false },
    { category: '', subcategory: 'Other Expenses', amount: plData.otherExpenses, isRevenue: false },
    { category: '', subcategory: 'Total Expenses', amount: plData.totalExpenses, isRevenue: false, isSubtotal: true },
    { category: 'NET PROFIT/LOSS', subcategory: '', amount: plData.netProfit, isTotal: true }
  ] : [];

  const columns = [
    { 
      title: 'Category', 
      dataIndex: 'category', 
      key: 'category',
      render: (text, record) => (
        <span style={{ fontWeight: record.isTotal || record.category ? 'bold' : 'normal' }}>
          {text}
        </span>
      )
    },
    { 
      title: 'Description', 
      dataIndex: 'subcategory', 
      key: 'subcategory',
      render: (text, record) => (
        <span style={{ 
          fontWeight: record.isTotal || record.isSubtotal ? 'bold' : 'normal',
          paddingLeft: record.category ? '0' : '20px'
        }}>
          {text}
        </span>
      )
    },
    { 
      title: 'Amount ($)', 
      dataIndex: 'amount', 
      key: 'amount',
      render: (amount, record) => {
        const color = record.isRevenue ? '#52c41a' : 
                     record.isTotal ? (amount >= 0 ? '#52c41a' : '#ff4d4f') : '#000';
        return (
          <span style={{ 
            color,
            fontWeight: record.isTotal || record.isSubtotal ? 'bold' : 'normal'
          }}>
            ${Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        );
      }
    }
  ];

  return (
    <div>
      <div className="no-print">
        <h1>Profit & Loss Statement</h1>
        
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={16} align="middle">
            <Col>
              <RangePicker 
                value={dateRange} 
                onChange={setDateRange}
                placeholder={['Start Date', 'End Date']}
                style={{ borderColor: !dateRange ? '#ff4d4f' : undefined }}
              />
            </Col>
            <Col>
              <Select value={selectedTruck} onChange={setSelectedTruck} style={{ width: 200 }}>
                <Option value="all">All Trucks</Option>
                {Array.isArray(trucks) && trucks.map(truck => {
                  const driver = drivers.find(d => d.truck_id === truck.truck_id);
                  return (
                    <Option key={truck.truck_id} value={truck.truck_id}>
                      {truck.license_plate} {driver ? `(${driver.name})` : ''}
                    </Option>
                  );
                })}
              </Select>
            </Col>
            <Col>
              <Button type="primary" onClick={generatePL} loading={loading}>
                Generate P&L Statement
              </Button>
            </Col>
            {plData && (
              <>
                <Col>
                  <Button 
                    icon={<FilePdfOutlined />} 
                    onClick={downloadPDF} 
                    loading={pdfLoading}
                  >
                    Download PDF
                  </Button>
                </Col>
                <Col>
                  <Button icon={<PrinterOutlined />} onClick={handlePrint}>
                    Print
                  </Button>
                </Col>
              </>
            )}
          </Row>
        </Card>
      </div>

      {plData && (
        <div ref={reportRef} className="print-content" style={{
          maxWidth: '210mm',
          margin: '0 auto',
          padding: '20mm',
          backgroundColor: 'white',
          minHeight: '297mm'
        }}>
          <div className="company-header" style={{
            textAlign: 'center',
            marginBottom: '40px',
            borderBottom: '2px solid #333',
            paddingBottom: '30px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '25px'
            }}>
              <img 
                src="/logo.png" 
                alt="DAN-CHI LOGISTICS" 
                style={{
                  height: '80px',
                  marginRight: '25px',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div style={{ display: 'none', width: '60px', height: '60px', backgroundColor: '#f0f0f0', borderRadius: '8px', alignItems: 'center', justifyContent: 'center', marginRight: '25px' }}>
                <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#666' }}>DC</span>
              </div>

            </div>
            <div>
              <h2 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: 'bold', color: '#333' }}>PROFIT & LOSS STATEMENT</h2>
              <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                <p style={{ margin: '8px 0' }}>
                  <strong>Period:</strong> {new Date(plData.startDate).toLocaleDateString('en-US', { 
                    year: 'numeric', month: 'long', day: 'numeric' 
                  })} - {new Date(plData.endDate).toLocaleDateString('en-US', { 
                    year: 'numeric', month: 'long', day: 'numeric' 
                  })}
                </p>
                <p style={{ margin: '8px 0' }}><strong>Scope:</strong> {plData.driverName}</p>
                <p style={{ margin: '8px 0' }}><strong>Generated:</strong> {new Date().toLocaleDateString('en-US', { 
                  year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                })}</p>
              </div>
            </div>
          </div>

          <Row gutter={[20, 20]} style={{ marginBottom: 50 }}>
            <Col span={6}>
              <Card 
                size="small" 
                style={{ 
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  backgroundColor: '#fafafa'
                }}
              >
                <Statistic
                  title={<span style={{ color: '#333', fontWeight: '500', fontSize: '13px' }}>Total Revenue</span>}
                  value={plData.revenue}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: '#333', fontSize: '18px', fontWeight: 'bold' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card 
                size="small"
                style={{ 
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  backgroundColor: '#fafafa'
                }}
              >
                <Statistic
                  title={<span style={{ color: '#333', fontWeight: '500', fontSize: '13px' }}>Total Expenses</span>}
                  value={plData.totalExpenses}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: '#333', fontSize: '18px', fontWeight: 'bold' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card 
                size="small"
                style={{ 
                  border: '2px solid #333',
                  borderRadius: '4px',
                  backgroundColor: '#f9f9f9'
                }}
              >
                <Statistic
                  title={<span style={{ 
                    color: '#333', 
                    fontWeight: 'bold',
                    fontSize: '13px'
                  }}>Net Profit/Loss</span>}
                  value={plData.netProfit}
                  precision={2}
                  prefix="$"
                  valueStyle={{ 
                    color: plData.netProfit >= 0 ? '#2d5016' : '#a8071a',
                    fontSize: '20px',
                    fontWeight: 'bold'
                  }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card 
                size="small"
                style={{ 
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  backgroundColor: '#fafafa'
                }}
              >
                <Statistic
                  title={<span style={{ color: '#333', fontWeight: '500', fontSize: '13px' }}>Profit Margin</span>}
                  value={plData.profitMargin}
                  precision={2}
                  suffix="%"
                  valueStyle={{ 
                    color: '#333',
                    fontSize: '18px',
                    fontWeight: 'bold'
                  }}
                />
              </Card>
            </Col>
          </Row>

          <Card 
            title={<span style={{ fontSize: '16px', fontWeight: 'bold', color: '#333' }}>Financial Statement</span>} 
            size="small"
            style={{
              border: '1px solid #d9d9d9',
              marginBottom: '30px'
            }}
            headStyle={{
              backgroundColor: '#f5f5f5',
              borderBottom: '1px solid #d9d9d9'
            }}
          >
            <Table
              dataSource={plTableData}
              columns={columns}
              pagination={false}
              rowKey={(record, index) => index}
              size="middle"
              showHeader={true}
              bordered
              rowClassName={(record) => {
                if (record.isTotal) return 'total-row';
                if (record.isSubtotal) return 'subtotal-row';
                return '';
              }}
            />
          </Card>

          <div style={{ 
            marginTop: '80px', 
            borderTop: '1px solid #d9d9d9',
            paddingTop: '20px'
          }}>
            <Row>
              <Col span={24}>
                <div style={{ textAlign: 'center' }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '14px' }}>Report Information</h4>
                  <p style={{ margin: '8px 0', fontSize: '12px', color: '#666', lineHeight: '1.5' }}>
                    <strong>Generated:</strong> {new Date().toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric', 
                      hour: '2-digit', minute: '2-digit'
                    })}
                  </p>
                  <p style={{ margin: '8px 0', fontSize: '12px', color: '#666', lineHeight: '1.5' }}>
                    <strong>Report Type:</strong> Profit & Loss Statement
                  </p>
                  <p style={{ margin: '8px 0', fontSize: '11px', color: '#999', lineHeight: '1.5' }}>
                    Confidential Financial Report
                  </p>
                </div>
              </Col>
            </Row>
          </div>

          {expenseBreakdown.length > 0 && (
            <div style={{ pageBreakBefore: 'always', paddingTop: '40px' }}>
              <div className="company-header" style={{
                textAlign: 'center',
                marginBottom: '40px',
                borderBottom: '2px solid #333',
                paddingBottom: '30px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '25px'
                }}>
                  <img 
                    src="/logo.png" 
                    alt="DAN-CHI LOGISTICS" 
                    style={{
                      height: '80px',
                      marginRight: '25px',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div style={{ display: 'none', width: '60px', height: '60px', backgroundColor: '#f0f0f0', borderRadius: '8px', alignItems: 'center', justifyContent: 'center', marginRight: '25px' }}>
                    <span style={{ fontSize: '20px', fontWeight: 'bold', color: '#666' }}>DC</span>
                  </div>
                </div>
                <div>
                  <h2 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: 'bold', color: '#333' }}>OTHER EXPENSES SUMMARY</h2>
                  <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                    <p style={{ margin: '8px 0' }}>
                      <strong>Period:</strong> {new Date(plData.startDate).toLocaleDateString('en-US', { 
                        year: 'numeric', month: 'long', day: 'numeric' 
                      })} - {new Date(plData.endDate).toLocaleDateString('en-US', { 
                        year: 'numeric', month: 'long', day: 'numeric' 
                      })}
                    </p>
                    <p style={{ margin: '8px 0' }}><strong>Scope:</strong> {plData.driverName}</p>
                  </div>
                </div>
              </div>
              
              <Card 
                title={<span style={{ fontSize: '16px', fontWeight: 'bold', color: '#333' }}>Expense Categories</span>} 
                size="small"
                style={{
                  border: '1px solid #d9d9d9',
                  marginBottom: '30px'
                }}
                headStyle={{
                  backgroundColor: '#f5f5f5',
                  borderBottom: '1px solid #d9d9d9'
                }}
              >
                <Table
                  dataSource={expenseBreakdown}
                  columns={[
                    { 
                      title: 'Category', 
                      dataIndex: 'category', 
                      key: 'category'
                    },
                    { 
                      title: 'Amount ($)', 
                      dataIndex: 'amount', 
                      key: 'amount',
                      render: (amount) => (
                        <span style={{ fontWeight: '500' }}>
                          ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      )
                    }
                  ]}
                  pagination={false}
                  rowKey={(record, index) => index}
                  size="middle"
                  bordered
                  summary={(pageData) => {
                    const totalAmount = pageData.reduce((sum, record) => sum + record.amount, 0);
                    return (
                      <Table.Summary.Row style={{ backgroundColor: '#f8f8f8', fontWeight: 'bold' }}>
                        <Table.Summary.Cell><strong>Total Other Expenses</strong></Table.Summary.Cell>
                        <Table.Summary.Cell>
                          <strong>${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    );
                  }}
                />
              </Card>
              
              <div style={{ 
                marginTop: '80px', 
                borderTop: '1px solid #d9d9d9',
                paddingTop: '20px'
              }}>
                <Row>
                  <Col span={24}>
                    <div style={{ textAlign: 'center' }}>
                      <h4 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '14px' }}>Report Information</h4>
                      <p style={{ margin: '8px 0', fontSize: '12px', color: '#666', lineHeight: '1.5' }}>
                        <strong>Generated:</strong> {new Date().toLocaleDateString('en-US', {
                          year: 'numeric', month: 'long', day: 'numeric', 
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                      <p style={{ margin: '8px 0', fontSize: '12px', color: '#666', lineHeight: '1.5' }}>
                        <strong>Report Type:</strong> Profit & Loss Statement
                      </p>
                      <p style={{ margin: '8px 0', fontSize: '11px', color: '#999', lineHeight: '1.5' }}>
                        Confidential Financial Report
                      </p>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          )}
        </div>
      )}

      <style jsx>{`
        @media print {
          .no-print { display: none !important; }
          .print-content { 
            margin: 0 !important;
            padding: 15mm !important;
            font-size: 11px;
            max-width: none !important;
          }
          .company-header { border-bottom: 2px solid #333 !important; }
          .company-header h1 { font-size: 22px !important; }
          .company-header h2 { font-size: 16px !important; }
          .ant-card { box-shadow: none !important; border: 1px solid #ccc !important; }
          .ant-statistic-content { font-size: 12px !important; }
          .total-row { background-color: #f0f0f0 !important; font-weight: bold !important; }
          .subtotal-row { background-color: #f8f8f8 !important; font-weight: bold !important; }
          div[style*="pageBreakBefore"] { 
            page-break-before: always !important;
          }
        }
        .total-row { 
          background-color: #f0f0f0 !important; 
          font-weight: bold; 
          border-top: 2px solid #333 !important;
          border-bottom: 2px solid #333 !important;
        }
        .subtotal-row { 
          background-color: #f8f8f8 !important; 
          font-weight: bold; 
        }
        .ant-table-thead > tr > th {
          background-color: #f5f5f5 !important;
          font-weight: bold !important;
          color: #333 !important;
          border-bottom: 2px solid #d9d9d9 !important;
        }
        .print-content {
          font-family: 'Arial', sans-serif;
        }
      `}</style>
    </div>
  );
};

export default ProfitLoss;